import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Eye,
  CreditCard,
  Smartphone,
  Banknote,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Edit,
  CheckCircle,
  Clock,
} from 'lucide-react';
import api from '../services/api';

const DEBOUNCE_MS = 400;

interface PaymentRow {
  id: string;
  orderId: string;
  paymentRecordId?: string;
  transactionId: string;
  order: {
    id: string;
    orderNumber: string;
    orderStatus?: string;
  };
  user: { firstName: string; lastName: string; email: string };
  paymentMethod: string;
  paymentType: 'PAYMENT' | 'REFUND';
  status: string;
  amount: number;
  currency: string;
  providerTransactionId?: string;
  providerReference?: string;
  phoneNumber?: string;
  paymentReference?: string;
  paymentMarkedPaidAt?: string;
  createdAt: string;
  updatedAt?: string;
  attemptedAt?: string;
  processedAt?: string;
  completedAt?: string;
  failedAt?: string;
  failureReason?: string;
  failureCode?: string;
}

interface PaymentStats {
  totalPayments: number;
  totalAmount: number;
  completedPayments: number;
  failedPayments: number;
  pendingPayments: number;
  byMethod: {
    _id: string;
    count: number;
    totalAmount: number;
    successCount: number;
  }[];
}

const Payments: React.FC = () => {
  const { t } = useTranslation();
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [selectedPayment, setSelectedPayment] = useState<PaymentRow | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusUpdateModal, setShowStatusUpdateModal] = useState(false);
  const [statusUpdateForm, setStatusUpdateForm] = useState({
    status: '',
    providerTransactionId: '',
    providerReference: '',
    failureReason: '',
    failureCode: '',
    notes: '',
  });
  const [stats, setStats] = useState<PaymentStats>({
    totalPayments: 0,
    totalAmount: 0,
    completedPayments: 0,
    failedPayments: 0,
    pendingPayments: 0,
    byMethod: [],
  });
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [listError, setListError] = useState<string | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [markingPaidId, setMarkingPaidId] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const isRefundView = filterType === 'REFUND';

  const fetchPaymentStats = useCallback(async () => {
    try {
      const endpoint = isRefundView ? 'admin/payments/stats' : 'admin/orders/payment-stats';
      const res = await api.get(endpoint);
      const data = res.data?.data ?? res.data ?? {};
      setStatsError(null);
      setStats({
        totalPayments: Number(data.totalPayments) || 0,
        totalAmount: Number(data.totalAmount) || 0,
        completedPayments: Number(data.completedPayments) || 0,
        failedPayments: Number(data.failedPayments) || 0,
        pendingPayments: Number(data.pendingPayments) || 0,
        byMethod: Array.isArray(data.byMethod) ? data.byMethod : [],
      });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Error fetching payment stats:', error);
      setStatsError(err.response?.data?.message || err.message || 'Failed to load stats');
    }
  }, [isRefundView]);

  const orderToPaymentRow = (order: Record<string, unknown>): PaymentRow => {
    const id = String(order.id ?? order._id ?? '');
    const user = (order.user as Record<string, string>) ?? {};
    const activePayment = order.activePayment as Record<string, unknown> | undefined;
    const apId = activePayment?.id ?? activePayment?._id;

    return {
      id,
      orderId: id,
      paymentRecordId: apId ? String(apId) : undefined,
      transactionId:
        (activePayment?.transactionId as string) ||
        (order.paymentReference as string) ||
        (order.orderNumber as string) ||
        id,
      order: {
        id,
        orderNumber: (order.orderNumber as string) ?? '—',
        orderStatus: order.orderStatus as string,
      },
      user: {
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      },
      paymentMethod: (order.paymentMethod as string) ?? 'CASH_ON_DELIVERY',
      paymentType: 'PAYMENT',
      status:
        (order.paymentStatus as string) ||
        (activePayment?.status as string) ||
        'PENDING',
      amount: Number(order.finalPrice ?? order.totalPrice ?? 0),
      currency:
        (order.currency as string) ||
        (activePayment?.currency as string) ||
        'USD',
      providerTransactionId: activePayment?.providerTransactionId as string | undefined,
      providerReference: activePayment?.providerReference as string | undefined,
      phoneNumber:
        (order.shippingAddress as { phoneNumber?: string })?.phoneNumber ||
        (activePayment?.phoneNumber as string | undefined),
      paymentReference: order.paymentReference as string | undefined,
      paymentMarkedPaidAt: order.paymentMarkedPaidAt as string | undefined,
      createdAt: (order.createdAt as string) ?? new Date().toISOString(),
      updatedAt: order.updatedAt as string | undefined,
      attemptedAt:
        (activePayment?.attemptedAt as string) ||
        (order.createdAt as string),
      processedAt: activePayment?.processedAt as string | undefined,
      completedAt:
        (activePayment?.completedAt as string) ||
        (order.paymentMarkedPaidAt as string | undefined),
      failedAt: activePayment?.failedAt as string | undefined,
      failureReason: activePayment?.failureReason as string | undefined,
      failureCode: activePayment?.failureCode as string | undefined,
    };
  };

  const paymentDocToRow = (p: Record<string, unknown>): PaymentRow => {
    const id = String(p.id ?? p._id ?? '');
    const order = (p.order as Record<string, unknown>) ?? {};
    const user = (p.user as Record<string, string>) ?? {};
    const orderId = String(order.id ?? order._id ?? p.order ?? '');

    return {
      id,
      orderId,
      paymentRecordId: id,
      transactionId: (p.transactionId as string) ?? id,
      order: {
        id: orderId,
        orderNumber: (order.orderNumber as string) ?? '—',
      },
      user: {
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      },
      paymentMethod: (p.paymentMethod as string) ?? 'CASH_ON_DELIVERY',
      paymentType: (p.paymentType as 'PAYMENT' | 'REFUND') ?? 'PAYMENT',
      status: (p.status as string) ?? 'PENDING',
      amount: Number(p.amount ?? 0),
      currency: (p.currency as string) ?? 'USD',
      providerTransactionId: p.providerTransactionId as string | undefined,
      providerReference: p.providerReference as string | undefined,
      phoneNumber: p.phoneNumber as string | undefined,
      createdAt: (p.createdAt as string) ?? new Date().toISOString(),
      updatedAt: p.updatedAt as string | undefined,
      attemptedAt: p.attemptedAt as string | undefined,
      processedAt: p.processedAt as string | undefined,
      completedAt: p.completedAt as string | undefined,
      failedAt: p.failedAt as string | undefined,
      failureReason: p.failureReason as string | undefined,
      failureCode: p.failureCode as string | undefined,
    };
  };

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);

      if (isRefundView) {
        const params = new URLSearchParams({
          page: currentPage.toString(),
          limit: entriesPerPage.toString(),
          paymentType: 'REFUND',
          ...(filterStatus && { status: filterStatus }),
          ...(filterMethod && { paymentMethod: filterMethod }),
          ...(searchTerm && { search: searchTerm }),
        });
        const response = await api.get(`admin/payments?${params}`);
        const payload = response.data?.data ?? response.data ?? {};
        const rows = Array.isArray(payload.payments)
          ? payload.payments.map((p: Record<string, unknown>) => paymentDocToRow(p))
          : [];
        setListError(null);
        setPayments(rows);
        setTotal(Number(payload.total) ?? rows.length);
        setTotalPages(Math.max(1, Number(payload.totalPages) ?? 1));
        return;
      }

      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: entriesPerPage.toString(),
        ...(filterStatus && { paymentStatus: filterStatus }),
        ...(filterMethod && { paymentMethod: filterMethod }),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await api.get(`admin/orders?${params}`);
      const payload = response.data?.data ?? response.data ?? {};
      const orders = Array.isArray(payload.orders) ? payload.orders : [];
      const normalized = orders.map((o: Record<string, unknown>) => orderToPaymentRow(o));
      setListError(null);
      setPayments(normalized);
      setTotal(Number(payload.total) ?? normalized.length);
      setTotalPages(Math.max(1, Number(payload.totalPages) ?? 1));
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      console.error('Error fetching payments:', error);
      setListError(err.response?.data?.message || err.message || 'Failed to load payments');
      setPayments([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, entriesPerPage, filterStatus, filterMethod, isRefundView]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput);
      setCurrentPage(1);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  useEffect(() => {
    fetchPaymentStats();
  }, [fetchPaymentStats]);

  const resetPageAndFilter = (setter: (v: string) => void, value: string) => {
    setter(value);
    setCurrentPage(1);
  };

  const isPaid = (row: PaymentRow) => row.status === 'COMPLETED';

  const isOrderDelivered = (row: PaymentRow) => row.order.orderStatus === 'COMPLETE';

  const canRecordPayment = (row: PaymentRow) =>
    isOrderDelivered(row) && row.order.orderStatus !== 'CANCELLED';

  const refreshRow = async (orderId: string) => {
    await fetchPayments();
    await fetchPaymentStats();
    if (selectedPayment && selectedPayment.orderId === orderId) {
      try {
        const response = await api.get(`admin/orders/${orderId}`);
        const data = response.data?.data ?? response.data;
        if (data) setSelectedPayment(orderToPaymentRow(data));
      } catch {
        // ignore
      }
    }
  };

  const handleMarkAsPaid = async (orderId: string, row?: PaymentRow) => {
    if (row && !canRecordPayment(row)) {
      return;
    }

    const paymentMethod =
      row?.paymentMethod ??
      (selectedPayment?.orderId === orderId ? selectedPayment.paymentMethod : undefined);
    const isCashOnDelivery = paymentMethod === 'CASH_ON_DELIVERY';
    let paymentReference: string | undefined;

    if (!isCashOnDelivery) {
      const reference = window.prompt(t('messages.paymentReferencePrompt', { ns: 'payments' }));
      if (reference === null) return;
      paymentReference = reference.trim() || undefined;
    }

    setMarkingPaidId(orderId);
    try {
      await api.put(`admin/orders/${orderId}/mark-paid`, {
        ...(paymentReference ? { paymentReference } : {}),
      });
      await refreshRow(orderId);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.errorUpdatingStatus', { ns: 'payments' }));
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleUpdateOrderPaymentStatus = async (orderId: string, newStatus: string) => {
    await api.put(`admin/orders/${orderId}/payment-status`, { paymentStatus: newStatus });
    await refreshRow(orderId);
  };

  const handleUpdatePaymentRecordStatus = async (
    paymentRecordId: string,
    orderId: string,
    payload: {
      status: string;
      providerTransactionId?: string;
      providerReference?: string;
      failureReason?: string;
      failureCode?: string;
      metadata?: Record<string, string>;
    }
  ) => {
    await api.put(`admin/payments/${paymentRecordId}/status`, payload);
    await refreshRow(orderId);
  };

  const handleStatusChange = async (row: PaymentRow, newStatus: string) => {
    if (newStatus === 'COMPLETED' && !isRefundView && !isOrderDelivered(row)) {
      return;
    }

    try {
      if (row.paymentRecordId && !isRefundView) {
        await handleUpdatePaymentRecordStatus(row.paymentRecordId, row.orderId, { status: newStatus });
      } else {
        await handleUpdateOrderPaymentStatus(row.orderId, newStatus);
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      console.error('Error updating payment status:', error);
      alert(err.response?.data?.message || t('messages.errorUpdatingStatus', { ns: 'payments' }));
    }
  };

  const handleViewDetails = async (payment: PaymentRow) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
    if (!isRefundView) {
      try {
        const response = await api.get(`admin/orders/${payment.orderId}`);
        const data = response.data?.data ?? response.data;
        if (data) setSelectedPayment(orderToPaymentRow(data));
      } catch {
        // keep list row data
      }
    }
  };

  const handleAdvancedStatusUpdate = (payment: PaymentRow) => {
    setSelectedPayment(payment);
    setStatusUpdateForm({
      status: payment.status,
      providerTransactionId: payment.providerTransactionId || '',
      providerReference: payment.providerReference || payment.paymentReference || '',
      failureReason: payment.failureReason || '',
      failureCode: payment.failureCode || '',
      notes: '',
    });
    setShowStatusUpdateModal(true);
  };

  const handleStatusUpdateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPayment) return;

    if (
      statusUpdateForm.status === 'COMPLETED' &&
      !isRefundView &&
      !isOrderDelivered(selectedPayment)
    ) {
      return;
    }

    setUpdatingStatus(true);
    try {
      const { status, providerTransactionId, providerReference, failureReason, failureCode, notes } =
        statusUpdateForm;

      if (selectedPayment.paymentRecordId && !isRefundView) {
        await handleUpdatePaymentRecordStatus(
          selectedPayment.paymentRecordId,
          selectedPayment.orderId,
          {
            status,
            providerTransactionId: providerTransactionId || undefined,
            providerReference: providerReference || undefined,
            failureReason: failureReason || undefined,
            failureCode: failureCode || undefined,
            metadata: notes ? { adminNotes: notes } : undefined,
          }
        );
      } else {
        await handleUpdateOrderPaymentStatus(selectedPayment.orderId, status);
      }
      setShowStatusUpdateModal(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      alert(err.response?.data?.message || t('messages.errorUpdatingStatus', { ns: 'payments' }));
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'PENDING':
        return 'badge-yellow';
      case 'PROCESSING':
        return 'bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium';
      case 'COMPLETED':
        return 'badge-green';
      case 'FAILED':
        return 'badge-red';
      case 'REFUNDED':
        return 'bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium';
      default:
        return 'badge-gray';
    }
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'MPESA':
      case 'ORANGE_MONEY':
      case 'AIRTEL_MONEY':
        return <Smartphone size={16} className="text-blue-600" />;
      case 'CASH_ON_DELIVERY':
        return <Banknote size={16} className="text-green-600" />;
      default:
        return <CreditCard size={16} className="text-gray-600" />;
    }
  };

  const getPaymentMethodName = (method: string) => {
    const keyMap: Record<string, string> = {
      MPESA: 'methods.mpesa',
      ORANGE_MONEY: 'methods.orangeMoney',
      AIRTEL_MONEY: 'methods.airtelMoney',
      CASH_ON_DELIVERY: 'methods.cashOnDelivery',
    };
    const key = keyMap[method];
    return key ? t(key, { ns: 'payments' }) : method;
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return '—';
    try {
      return new Date(dateString).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '—';
    }
  };

  const formatPrice = (price: number | undefined | null, currency: string = 'USD') => {
    if (price === undefined || price === null || Number.isNaN(price)) {
      return `0 ${currency}`;
    }
    return `${price.toLocaleString()} ${currency}`;
  };

  const getSuccessRate = (method: { count: number; successCount: number }) => {
    if (method.count === 0) return '0.0';
    return ((method.successCount / method.count) * 100).toFixed(1);
  };

  const renderStatusControl = (row: PaymentRow) => {
    if (isPaid(row)) {
      return (
        <div className="flex flex-col gap-1">
          <span
            className={`inline-flex items-center gap-1 text-xs py-1 px-2 rounded-full font-medium w-fit ${getStatusBadgeClass('COMPLETED')}`}
          >
            <CheckCircle size={12} />
            {t('status.completed', { ns: 'payments' })}
          </span>
          {row.paymentMarkedPaidAt && (
            <span className="text-xs text-gray-500">
              {t('messages.paidRecordedAt', {
                ns: 'payments',
                date: new Date(row.paymentMarkedPaidAt).toLocaleString(),
              })}
            </span>
          )}
        </div>
      );
    }

    if (isRefundView) {
      return (
        <select
          value={row.status}
          onChange={(e) => handleStatusChange(row, e.target.value)}
          className={`text-xs py-1 px-2 rounded-full border-0 font-medium ${getStatusBadgeClass(row.status)}`}
        >
          <option value="PENDING">{t('status.pending', { ns: 'payments' })}</option>
          <option value="PROCESSING">{t('status.processing', { ns: 'payments' })}</option>
          <option value="COMPLETED">{t('status.completed', { ns: 'payments' })}</option>
          <option value="FAILED">{t('status.failed', { ns: 'payments' })}</option>
          <option value="REFUNDED">{t('status.refunded', { ns: 'payments' })}</option>
          <option value="CANCELLED">{t('status.cancelled', { ns: 'payments' })}</option>
        </select>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          disabled={
            row.order.orderStatus === 'CANCELLED' ||
            !canRecordPayment(row) ||
            markingPaidId === row.orderId
          }
          onClick={() => handleMarkAsPaid(row.orderId, row)}
          title={t('actions.markAsPaid', { ns: 'payments' })}
          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] leading-tight font-medium rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-50 whitespace-nowrap"
        >
          <CheckCircle size={11} />
          {markingPaidId === row.orderId
            ? t('actions.markingAsPaidShort', { ns: 'payments', defaultValue: '…' })
            : t('actions.markAsPaidShort', { ns: 'payments', defaultValue: 'Paid' })}
        </button>
        {row.paymentRecordId && (
          <select
            value={row.status}
            onChange={(e) => handleStatusChange(row, e.target.value)}
            className={`text-[10px] py-0.5 px-1 rounded border border-gray-200 max-w-[5.5rem] ${getStatusBadgeClass(row.status)}`}
          >
            <option value="PENDING">{t('status.pending', { ns: 'payments' })}</option>
            <option value="PROCESSING">{t('status.processing', { ns: 'payments' })}</option>
            <option value="COMPLETED" disabled={!isOrderDelivered(row)}>
              {t('status.completed', { ns: 'payments' })}
            </option>
            <option value="FAILED">{t('status.failed', { ns: 'payments' })}</option>
            <option value="REFUNDED">{t('status.refunded', { ns: 'payments' })}</option>
            <option value="CANCELLED">{t('status.cancelled', { ns: 'payments' })}</option>
          </select>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">{t('title', { ns: 'payments' })}</h1>
        <p className="text-gray-600 mt-2">{t('subtitle', { ns: 'payments' })}</p>
      </div>

      {(listError || statsError) && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between flex-wrap gap-2">
          <p className="text-red-800 text-sm">{listError || statsError}</p>
          <button
            type="button"
            onClick={() => {
              setListError(null);
              setStatsError(null);
              fetchPaymentStats();
              fetchPayments();
            }}
            className="text-sm font-medium text-red-700 hover:text-red-800 underline"
          >
            {t('actions.retry', { ns: 'payments' })}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.totalPayments', { ns: 'payments' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPayments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.totalAmount', { ns: 'payments' })}</p>
              <p className="text-2xl font-bold text-gray-900">{formatPrice(stats.totalAmount)}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.successful', { ns: 'payments' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedPayments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-amber-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.pending', { ns: 'payments' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingPayments}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <TrendingDown className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">{t('stats.failed', { ns: 'payments' })}</p>
              <p className="text-2xl font-bold text-gray-900">{stats.failedPayments}</p>
            </div>
          </div>
        </div>
      </div>

      {stats.byMethod.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('methodsPerformance.title', { ns: 'payments' })}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {stats.byMethod.map((method) => (
              <div key={method._id} className="border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    {getPaymentMethodIcon(method._id)}
                    <span className="font-medium">{getPaymentMethodName(method._id)}</span>
                  </div>
                  <span className="text-sm text-green-600 font-medium">{getSuccessRate(method)}%</span>
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  <div className="flex justify-between">
                    <span>{t('methodsPerformance.transactions', { ns: 'payments' })}:</span>
                    <span className="font-medium">{method.count}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('methodsPerformance.amount', { ns: 'payments' })}:</span>
                    <span className="font-medium">{formatPrice(method.totalAmount)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>{t('methodsPerformance.success', { ns: 'payments' })}:</span>
                    <span className="font-medium text-green-600">{method.successCount}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={filterStatus}
              onChange={(e) => resetPageAndFilter(setFilterStatus, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('filters.allStatus', { ns: 'payments' })}</option>
              <option value="PENDING">{t('status.pending', { ns: 'payments' })}</option>
              <option value="PROCESSING">{t('status.processing', { ns: 'payments' })}</option>
              <option value="COMPLETED">{t('status.completed', { ns: 'payments' })}</option>
              <option value="FAILED">{t('status.failed', { ns: 'payments' })}</option>
              <option value="REFUNDED">{t('status.refunded', { ns: 'payments' })}</option>
              <option value="CANCELLED">{t('status.cancelled', { ns: 'payments' })}</option>
            </select>

            <select
              value={filterMethod}
              onChange={(e) => resetPageAndFilter(setFilterMethod, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={isRefundView}
            >
              <option value="">{t('filters.allMethods', { ns: 'payments' })}</option>
              <option value="MPESA">{t('methods.mpesa', { ns: 'payments' })}</option>
              <option value="ORANGE_MONEY">{t('methods.orangeMoney', { ns: 'payments' })}</option>
              <option value="AIRTEL_MONEY">{t('methods.airtelMoney', { ns: 'payments' })}</option>
              <option value="CASH_ON_DELIVERY">{t('methods.cashOnDelivery', { ns: 'payments' })}</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => resetPageAndFilter(setFilterType, e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="">{t('filters.allTypes', { ns: 'payments' })}</option>
              <option value="PAYMENT">{t('types.payment', { ns: 'payments' })}</option>
              <option value="REFUND">{t('types.refund', { ns: 'payments' })}</option>
            </select>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700">{t('filters.show', { ns: 'payments' })}</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-700">{t('filters.entries', { ns: 'payments' })}</span>
            </div>

            <div className="relative">
              <span className="text-sm text-gray-700 mr-2">{t('filters.search', { ns: 'payments' })}</span>
              <input
                type="text"
                placeholder={t('search', { ns: 'payments' })}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm w-64"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.transaction', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.order', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.customer', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.method', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.type', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.status', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.amount', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.date', { ns: 'payments' })}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('table.actions', { ns: 'payments' })}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : payments.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    {t('messages.noPaymentsFound', { ns: 'payments' })}
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={`${payment.id}-${payment.paymentRecordId ?? 'order'}`} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{payment.transactionId}</div>
                        {payment.providerTransactionId && (
                          <div className="text-xs text-gray-500">
                            {t('table.provider', { ns: 'payments' })}: {payment.providerTransactionId}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">#{payment.order?.orderNumber ?? '—'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {[payment.user?.firstName, payment.user?.lastName].filter(Boolean).join(' ') || '—'}
                        </div>
                        <div className="text-sm text-gray-500">{payment.user?.email ?? '—'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getPaymentMethodIcon(payment.paymentMethod)}
                        <span className="text-sm">{getPaymentMethodName(payment.paymentMethod)}</span>
                      </div>
                      {payment.phoneNumber && (
                        <div className="text-xs text-gray-500">{payment.phoneNumber}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          payment.paymentType === 'REFUND'
                            ? 'bg-orange-100 text-orange-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {payment.paymentType === 'REFUND'
                          ? t('types.refund', { ns: 'payments' })
                          : t('types.payment', { ns: 'payments' })}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">{renderStatusControl(payment)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(payment.amount, payment.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(payment.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          onClick={() => handleViewDetails(payment)}
                          className="text-primary-600 hover:text-primary-700 p-1"
                          title={t('actions.viewDetails', { ns: 'payments' })}
                        >
                          <Eye size={16} />
                        </button>
                        {!isPaid(payment) && payment.paymentRecordId && (
                          <button
                            type="button"
                            onClick={() => handleAdvancedStatusUpdate(payment)}
                            className="text-blue-600 hover:text-blue-700 p-1"
                            title={t('actions.editStatus', { ns: 'payments' })}
                          >
                            <Edit size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-gray-500">
            {total === 0
              ? t('messages.noPaymentsFound', { ns: 'payments' })
              : `${t('table.showing', { ns: 'common' })} ${(currentPage - 1) * entriesPerPage + 1} ${t('table.to', { ns: 'common' })} ${Math.min(currentPage * entriesPerPage, total)} ${t('table.of', { ns: 'common' })} ${total} ${t('table.results', { ns: 'common' })}`}
            {total > 0 && (
              <>
                {' '}
                — {t('table.page', { ns: 'common' })} {currentPage} {t('table.of', { ns: 'common' })} {totalPages}
              </>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1 || loading}
              className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
            >
              {t('table.previous', { ns: 'common' })}
            </button>
            <span className="text-sm text-gray-700">
              {currentPage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages || loading}
              className="btn-outline text-sm py-1 px-2 disabled:opacity-50"
            >
              {t('table.next', { ns: 'common' })}
            </button>
          </div>
        </div>
      </div>

      {showDetailsModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('modal.paymentDetails', { ns: 'payments' })} — {selectedPayment.transactionId}
              </h3>
              <button
                type="button"
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <div className="space-y-6">
              <div className="card p-4">
                <h4 className="font-medium text-gray-900 mb-3 flex items-center">
                  <CreditCard size={16} className="mr-2" />
                  {t('modal.paymentInformation', { ns: 'payments' })}
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">{t('fields.transactionId', { ns: 'payments' })}:</span>
                    <div className="mt-1">{selectedPayment.transactionId}</div>
                  </div>
                  <div>
                    <span className="font-medium">{t('fields.order', { ns: 'payments' })}:</span>
                    <div className="mt-1">#{selectedPayment.order?.orderNumber}</div>
                  </div>
                  <div>
                    <span className="font-medium">{t('fields.status', { ns: 'payments' })}:</span>
                    <div className="mt-1">
                      <span className={getStatusBadgeClass(selectedPayment.status)}>
                        {t(`status.${selectedPayment.status.toLowerCase()}`, {
                          ns: 'payments',
                          defaultValue: selectedPayment.status,
                        })}
                      </span>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">{t('fields.method', { ns: 'payments' })}:</span>
                    <div className="mt-1 flex items-center space-x-2">
                      {getPaymentMethodIcon(selectedPayment.paymentMethod)}
                      <span>{getPaymentMethodName(selectedPayment.paymentMethod)}</span>
                    </div>
                  </div>
                  <div>
                    <span className="font-medium">{t('fields.amount', { ns: 'payments' })}:</span>
                    <div className="mt-1 text-lg font-semibold">
                      {formatPrice(selectedPayment.amount, selectedPayment.currency)}
                    </div>
                  </div>
                  {selectedPayment.paymentMethod !== 'CASH_ON_DELIVERY' &&
                    selectedPayment.paymentReference && (
                    <div>
                      <span className="font-medium">{t('fields.providerReference', { ns: 'payments' })}:</span>
                      <div className="mt-1">{selectedPayment.paymentReference}</div>
                    </div>
                  )}
                </div>

                {!isPaid(selectedPayment) && !isRefundView && (
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <button
                      type="button"
                      disabled={
                        markingPaidId === selectedPayment.orderId ||
                        !canRecordPayment(selectedPayment)
                      }
                      onClick={() => handleMarkAsPaid(selectedPayment.orderId, selectedPayment)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
                    >
                      <CheckCircle size={14} />
                      {markingPaidId === selectedPayment.orderId
                        ? t('actions.markingAsPaid', { ns: 'payments' })
                        : t('actions.markAsPaid', { ns: 'payments' })}
                    </button>
                  </div>
                )}
              </div>

              {selectedPayment.paymentMethod !== 'CASH_ON_DELIVERY' &&
                (selectedPayment.providerTransactionId || selectedPayment.providerReference) && (
                <div className="card p-4">
                  <h4 className="font-medium text-gray-900 mb-3">
                    {t('modal.providerInformation', { ns: 'payments' })}
                  </h4>
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    {selectedPayment.providerTransactionId && (
                      <div>
                        <span className="font-medium">{t('fields.providerTransactionId', { ns: 'payments' })}:</span>
                        <div className="mt-1">{selectedPayment.providerTransactionId}</div>
                      </div>
                    )}
                    {selectedPayment.providerReference && (
                      <div>
                        <span className="font-medium">{t('fields.providerReference', { ns: 'payments' })}:</span>
                        <div className="mt-1">{selectedPayment.providerReference}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="card p-4">
                <h4 className="font-medium text-gray-900 mb-3">{t('modal.timeline', { ns: 'payments' })}</h4>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">{t('modal.created', { ns: 'payments' })}:</span>
                    <span className="ml-2">{formatDate(selectedPayment.createdAt)}</span>
                  </div>
                  {selectedPayment.paymentMarkedPaidAt && (
                    <div>
                      <span className="font-medium">
                        {t('messages.paidRecordedAt', {
                          ns: 'payments',
                          date: new Date(selectedPayment.paymentMarkedPaidAt).toLocaleString(),
                        })}
                      </span>
                    </div>
                  )}
                  {selectedPayment.completedAt && (
                    <div>
                      <span className="font-medium">{t('modal.completed', { ns: 'payments' })}:</span>
                      <span className="ml-2">{formatDate(selectedPayment.completedAt)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showStatusUpdateModal && selectedPayment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('modal.updatePaymentStatus', { ns: 'payments' })}
              </h3>
              <button
                type="button"
                onClick={() => setShowStatusUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleStatusUpdateSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.paymentStatus', { ns: 'payments' })} *
                </label>
                <select
                  value={statusUpdateForm.status}
                  onChange={(e) => setStatusUpdateForm({ ...statusUpdateForm, status: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  required
                >
                  <option value="PENDING">{t('status.pending', { ns: 'payments' })}</option>
                  <option value="PROCESSING">{t('status.processing', { ns: 'payments' })}</option>
                  <option
                    value="COMPLETED"
                    disabled={selectedPayment ? !isOrderDelivered(selectedPayment) : false}
                  >
                    {t('status.completed', { ns: 'payments' })}
                  </option>
                  <option value="FAILED">{t('status.failed', { ns: 'payments' })}</option>
                  <option value="REFUNDED">{t('status.refunded', { ns: 'payments' })}</option>
                  <option value="CANCELLED">{t('status.cancelled', { ns: 'payments' })}</option>
                </select>
              </div>

              {selectedPayment.paymentMethod !== 'CASH_ON_DELIVERY' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('fields.providerTransactionId', { ns: 'payments' })}
                    </label>
                    <input
                      type="text"
                      value={statusUpdateForm.providerTransactionId}
                      onChange={(e) =>
                        setStatusUpdateForm({
                          ...statusUpdateForm,
                          providerTransactionId: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('fields.providerReference', { ns: 'payments' })}
                    </label>
                    <input
                      type="text"
                      value={statusUpdateForm.providerReference}
                      onChange={(e) =>
                        setStatusUpdateForm({
                          ...statusUpdateForm,
                          providerReference: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}

              {statusUpdateForm.status === 'FAILED' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('fields.failureReason', { ns: 'payments' })}
                    </label>
                    <input
                      type="text"
                      value={statusUpdateForm.failureReason}
                      onChange={(e) =>
                        setStatusUpdateForm({ ...statusUpdateForm, failureReason: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('fields.failureCode', { ns: 'payments' })}
                    </label>
                    <input
                      type="text"
                      value={statusUpdateForm.failureCode}
                      onChange={(e) =>
                        setStatusUpdateForm({ ...statusUpdateForm, failureCode: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('fields.adminNotes', { ns: 'payments' })}
                </label>
                <textarea
                  value={statusUpdateForm.notes}
                  onChange={(e) => setStatusUpdateForm({ ...statusUpdateForm, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowStatusUpdateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  {t('actions.cancel', { ns: 'payments' })}
                </button>
                <button
                  type="submit"
                  disabled={updatingStatus}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {updatingStatus
                    ? t('actions.updating', { ns: 'payments' })
                    : t('actions.updateStatus', { ns: 'payments' })}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Payments;
